/* reviews.api.js */
export function createReviewsApi(
  ReviewConfProducts,
  { cookieName = 'reviews_auth', refreshTokenEachRequest = false } = {}
) {
  if (!ReviewConfProducts?.domain) throw new Error('Reviews config is missing');

  const storageKey = cookieName;

  const getCookie = (name) => {
    const dc = document.cookie || '';
    const prefix = `${encodeURIComponent(name)}=`;
    const parts = dc.split(';').map((p) => p.trim());
    const found = parts.find((p) => p.startsWith(prefix));
    if (!found) return null;
    return decodeURIComponent(found.substring(prefix.length));
  };

  const setCookie = (name, value, expiresAtIso) => {
    const expires = expiresAtIso ? new Date(expiresAtIso) : null;
    const expStr = expires ? `; Expires=${expires.toUTCString()}` : '';
  
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; Path=/; Secure; SameSite=Lax${expStr}`;
  };

  const deleteCookie = (name) => {
    const encodedName = encodeURIComponent(name);
  
    document.cookie = `${encodedName}=; Path=/; Max-Age=0; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Secure; SameSite=Lax`;
  };

  const stripContentType = (headersObj = {}) => {
    const h = { ...(headersObj || {}) };
    Object.keys(h).forEach((k) => {
      if (k.toLowerCase() === 'content-type') delete h[k];
    });
    return h;
  };

  async function fetchJson(url, { method = 'GET', headers = {}, body = null } = {}) {
    const res = await fetch(url, { method, headers, body, credentials: 'same-origin' });

    const contentType = res.headers.get('content-type') || '';
    const data = contentType.includes('application/json') ? await res.json() : await res.text();

    if (!res.ok) {
      const err = new Error(`HTTP ${res.status}`);
      err.status = res.status;
      err.data = data;
      throw err;
    }

    return data;
  }

  async function fetchWithAuthJson(url, { method = 'GET', token = null, headers = {}, bodyObj = null } = {}) {
    const finalHeaders = { Accept: 'application/json', ...headers };
    if (token) finalHeaders.Authorization = token;

    let body = null;
    if (bodyObj) {
      finalHeaders['Content-Type'] = 'application/json';
      body = JSON.stringify(bodyObj);
    }

    return fetchJson(url, { method, headers: finalHeaders, body });
  }

  async function fetchWithAuthFormData(url, { token, formData, headers = {} } = {}) {
    const safeHeaders = stripContentType(headers);
    const finalHeaders = { Accept: 'application/json', ...safeHeaders };
    if (token) finalHeaders.Authorization = token;

    return fetchJson(url, { method: 'POST', headers: finalHeaders, body: formData });
  }

  function purgeToken() {
    sessionStorage.removeItem(storageKey);
    deleteCookie(cookieName);
  }

  async function requestWithReauth(makeRequest) {
    try {
      return await makeRequest();
    } catch (e) {
      if (e?.status !== 401) throw e;
      purgeToken();
      return await makeRequest();
    }
  }

  async function getToken({ forceRefresh = refreshTokenEachRequest } = {}) {
    if (forceRefresh) {
      purgeToken();
    }

    const ss = sessionStorage.getItem(storageKey);
    if (ss) return ss;

    const cookieToken = getCookie(cookieName);
    if (cookieToken) {
      sessionStorage.setItem(storageKey, cookieToken);
      return cookieToken;
    }

    const url = `${ReviewConfProducts.domain}${ReviewConfProducts.methods.auth}`;
    const body = { key: ReviewConfProducts.key, secret: ReviewConfProducts.secret };

    const res = await fetchWithAuthJson(url, {
      method: 'POST',
      bodyObj: body,
      headers: { 'Content-Type': 'application/json', ...(ReviewConfProducts.headers || {}) },
    });

    const token = `${res.type} ${res.token}`;
    setCookie(cookieName, token, res.expired_at);
    sessionStorage.setItem(storageKey, token);
    return token;
  }

  function buildUrl(method, params = {}) {
    const u = new URL(`${ReviewConfProducts.domain}${method}`);
    u.searchParams.set('shop', ReviewConfProducts.shop_name);

    Object.entries(params).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      u.searchParams.set(k, String(v));
    });

    return u.toString();
  }

  async function canWrite({ customer_id, product_id, order_id }) {
    const url = buildUrl(ReviewConfProducts.methods.feedbacksCanWrite);

    return requestWithReauth(async () => {
      const token = await getToken();
      return fetchWithAuthJson(url, {
        method: 'POST',
        token,
        headers: ReviewConfProducts.headers || {},
        bodyObj: { customer_id, product_id, order_id },
      });
    });
  }

  async function store({ formData }) {
    const url = buildUrl(ReviewConfProducts.methods.feedbacksStore);

    return requestWithReauth(async () => {
      const token = await getToken();

      return fetchWithAuthFormData(url, {
        token,
        formData,
        headers: ReviewConfProducts.headers || {},
      });
    });
  }

  async function canWriteOrders({ customer_id, orders }) {
    if (!customer_id) throw new Error('canWriteOrders: customer_id is required');
    if (!Array.isArray(orders)) throw new Error('canWriteOrders: orders must be an array');

    const url = buildUrl(ReviewConfProducts.methods.feedbacksCanWriteOrders);

    return requestWithReauth(async () => {
      const token = await getToken();
      return fetchWithAuthJson(url, {
        method: 'POST',
        token,
        headers: ReviewConfProducts.headers || {},
        bodyObj: { customer_id, orders },
      });
    });
  }

  async function submitReview({
    formEl = null,
    payload = null,
    precheck = true,
    customer_id,
    product_id,
    order_id,
  } = {}) {
    if (!formEl && !payload) throw new Error('submitReview: provide formEl or payload');
    if (!customer_id || !product_id) throw new Error('submitReview: customer_id and product_id are required');

    if (precheck) {
      await canWrite({ customer_id, product_id, order_id });
    }

    const fd = formEl ? new FormData(formEl) : new FormData();

    if (!formEl && payload) {
      Object.entries(payload).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        fd.append(k, v);
      });
    }

    if (!fd.get('customer_id')) fd.append('customer_id', String(customer_id));
    if (!fd.get('product_id')) fd.append('product_id', String(product_id));
    if (order_id && !fd.get('order_id')) fd.append('order_id', String(order_id));

    // ✅ прибираємо пустий файл (filename="")
    const img = fd.get('images[0]');
    if (img instanceof File && (!img.name || img.size === 0)) {
      fd.delete('images[0]');
    }

    try {
      const res = await store({ formData: fd });
      return { ok: true, res };
    } catch (e) {
      const serverErrors = e?.data?.errors || null;
      return {
        ok: false,
        status: e?.status || 0,
        error: e,
        serverErrors,
        imageError: serverErrors?.['images.0']?.[0] || serverErrors?.['images[0]']?.[0] || null,
      };
    }
  }

  return {
    getToken,
    purgeToken,
    canWriteOrders,
    canWrite,
    submitReview,
  };
}
